import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_WORK_ORDER_PRIORITIES } from '@lims/shared-types';
import { workOrdersApi } from '../../api/endpoints.js';
import type { WorkOrderSummary } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordNumber, ViewTabs } from '../clients/record-layout.js';
import { toneForWorkOrderStatus } from './status.js';

/**
 * The work-order list.
 *
 * ===========================================================================
 * THE STATUS COLUMN RENDERS WHAT THE BACKEND DERIVED. IT IS NOT RECOMPUTED.
 * ===========================================================================
 *
 * `status` and `isOverdue` are both derived server-side — the first from the
 * states of the requested services, the second from the promised date against
 * the laboratory's own calendar day. Recomputing either here would be a second
 * implementation of the same rule, free to disagree with the first, and the
 * list would then report a job as late that the file called on time.
 *
 * PROGRESS IS SHOWN AS A COUNT, NEVER A PERCENTAGE. "3 of 5" cannot be rounded
 * up to "finished" in a reader's head; "60%" invites exactly that.
 *
 * THE STATUS FILTER INCLUDES `partially_complete` AS ITS OWN CHOICE. A list
 * that offered only "complete" and "in progress" would force partial work into
 * one bucket or the other, and either answer would be a false claim.
 */

/** The derived statuses a request can actually report. `new` is never one. */
const FILTERABLE_STATUSES = [
  'awaiting_sample',
  'in_progress',
  'partially_complete',
  'complete',
  'cancelled',
] as const;

export function WorkOrdersPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [overdue, setOverdue] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const workOrders = useQuery({
    queryKey: ['work-orders', { page, search, status, priority, overdue }],
    queryFn: () =>
      workOrdersApi.list({
        page,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(priority ? { priority } : {}),
        ...(overdue ? { overdue } : {}),
      }),
  });

  const columns: readonly DataColumn<WorkOrderSummary>[] = [
    {
      key: 'number',
      header: t('workOrders.number'),
      render: (order) => (
        // A Job Number is a machine identifier: always left-to-right, in both
        // languages, so its digits never reorder under Arabic.
        // The number opens the file: the row's way in (D278).
        <RecordNumber to={`/work-orders/${order.id}`}>{order.workOrderNumber}</RecordNumber>
      ),
    },
    {
      key: 'client',
      header: t('workOrders.client'),
      render: (order) => (
        <div className="min-w-0">
          <span className="block truncate text-default-800" dir="auto">
            {order.clientName ?? t('common.notRecorded')}
          </span>
          {order.clientNumber ? (
            <span className="measure text-caption text-default-600" dir="ltr">
              {order.clientNumber}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('workOrders.statusLabel'),
      render: (order) => (
        // DERIVED by the backend. Rendered, never recalculated.
        <StatusBadge
          label={t(`status.workOrder.${order.status}`)}
          tone={toneForWorkOrderStatus(order.status)}
        />
      ),
    },
    {
      key: 'progress',
      header: t('workOrders.progress'),
      render: (order) => (
        <div className="min-w-0">
          {/* A count, not a percentage. */}
          <span className="measure block text-default-800" dir="ltr">
            {t('workOrders.progressCount', {
              done: order.progress.completed,
              total: order.progress.total,
            })}
          </span>
          {order.progress.outstanding > 0 ? (
            <span className="text-caption text-default-600">
              {t('workOrders.outstandingCount', {
                count: order.progress.outstanding,
              })}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'dueDate',
      header: t('workOrders.dueDate'),
      render: (order) => (
        <div className="min-w-0">
          {/*
            No date is NOT today and not a blank waiting to be filled: it is
            the laboratory having promised nothing, which the column says in
            words rather than with a dash.
          */}
          <span className="block whitespace-nowrap text-default-700">
            {order.dueDate
              ? formatDate(order.dueDate, { locale })
              : t('workOrders.dueNotPromised')}
          </span>
          {order.isOverdue ? (
            <StatusBadge size="sm" tone="warning" label={t('workOrders.overdueBadge')} />
          ) : null}
        </div>
      ),
    },
    {
      key: 'total',
      header: t('workOrders.total'),
      align: 'end',
      render: (order) => (
        <span className="measure text-default-900" dir="ltr">
          {formatMoney(order.totalMinor, {
            locale,
            currency: order.currency,
          })}
        </span>
      ),
    },
    /* No separate "Open" column: the job number in the first column is the
       link to the record (D278), and the table then fits a laptop. */
  ];

  return (
    <>
      <PageHeader
        title={t('workOrders.title')}
        subtitle={t('workOrders.subtitle')}
        breadcrumbs={[{ label: t('nav.workOrders') }]}
        actions={
          can(PERMISSIONS.WORK_ORDERS_CREATE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/work-orders/new')}
            >
              {t('workOrders.create')}
            </ActionButton>
          ) : null
        }
      />

      <ViewTabs
        label={t('workOrders.statusLabel')}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          { value: '', label: t('common.all') },
          ...FILTERABLE_STATUSES.map((value) => ({
            value,
            label: t(`status.workOrder.${value}`),
          })),
        ]}
      />

      <Panel>
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('workOrders.searchPlaceholder')}
          />
          <SelectField
            label={t('workOrders.priorityLabel')}
            value={priority}
            onChange={(value) => {
              setPriority(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={ALL_WORK_ORDER_PRIORITIES.map((value) => ({
              value,
              label: t(`workOrderPriority.${value}`),
            }))}
          />
          {/*
            Lateness is DERIVED, so it is its own filter rather than a status.
            "Overdue" is not a state a request is in; it is a fact about the date
            the laboratory promised, and a job can be overdue and in progress at
            the same time.
          */}
          <SelectField
            label={t('workOrders.overdueLabel')}
            value={overdue}
            onChange={(value) => {
              setOverdue(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={[
              { value: 'true', label: t('workOrders.overdueOnly') },
              { value: 'false', label: t('workOrders.onTimeOnly') },
            ]}
          />
        </FilterBar>

        <DataTable
          label={t('workOrders.title')}
          columns={columns}
          rows={workOrders.data?.data ?? []}
          rowKey={(order) => order.id}
          isLoading={workOrders.isLoading}
          error={workOrders.error}
          onRetry={() => void workOrders.refetch()}
          emptyContent={
            search || status || priority || overdue
              ? t('workOrders.noneFound')
              : t('workOrders.noneYet')
          }
          page={page}
          totalPages={workOrders.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
